import React, { useState, useCallback, useEffect } from 'react';
import { ImageCarouselProps } from '@/services/mockAPI/mockData.types';
import { ChevronLeft, ChevronRight, Expand } from 'lucide-react';
import { Button } from '../ui/button';

export const ImageCarousel = ({ images }: ImageCarouselProps) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [expanded, setExpanded] = useState(false);

  const handlePrev = useCallback(() => {
    setActiveIndex(prev => (prev <= 0 ? images.length - 1 : prev - 1));
  }, [images]);

  const handleNext = useCallback(() => {
    setActiveIndex(prev => (prev >= images.length - 1 ? 0 : prev + 1));
  }, [images]);

  const handleExpandedToggle = useCallback(() => {
    if (!expanded) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    setExpanded(prev => !prev);
  }, [expanded]);

  useEffect(() => {
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  return (
    <>
      {expanded && (
        <div
          className="fixed inset-0 bg-black/60 z-40 backdrop-blur-sm cursor-pointer"
          onClick={handleExpandedToggle}
          role="button"
          tabIndex={0}
          onKeyDown={e => {
            if (e.key === 'Escape') {
              handleExpandedToggle();
            }
          }}
          aria-label="Close expanded view"
        />
      )}

      <div
        className={`border-2 border-slate-200 rounded-xl overflow-hidden bg-white ${
          expanded
            ? 'fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-[95vw] max-w-6xl shadow-2xl'
            : 'w-full max-w-3xl shadow-lg'
        }`}>
        <div className="relative px-4 py-3 bg-white border-b border-slate-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-[#252F33] flex-1 text-center">
            Inspection Images
          </h3>
          <button
            onClick={handleExpandedToggle}
            className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
            aria-label={expanded ? 'Collapse carousel' : 'Expand carousel'}>
            <Expand className="h-5 w-5 text-slate-600" />
          </button>
        </div>

        <div className="relative w-full aspect-video bg-slate-100">
          <img
            key={images[activeIndex].id}
            src={images[activeIndex].url}
            alt={images[activeIndex].description}
            className="absolute inset-0 w-full h-full object-cover"
          />

          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4">
            <p className="text-white font-medium text-sm">
              {images[activeIndex].description}
            </p>
          </div>
        </div>

        <div className="flex items-center justify-between p-4 bg-slate-50 border-t border-slate-200">
          <Button
            onClick={handlePrev}
            variant="outline"
            size="sm"
            className="gap-1">
            <ChevronLeft className="h-4 w-4" />
            Previous
          </Button>

          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-600">
              {activeIndex + 1} / {images.length}
            </span>
          </div>

          <Button
            onClick={handleNext}
            variant="outline"
            size="sm"
            className="gap-1">
            Next
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>

        <div className="flex items-center justify-center gap-2 p-3 bg-white border-t border-slate-200">
          {images.map((_, index) => (
            <button
              key={index}
              onClick={() => setActiveIndex(index)}
              className={`h-2 rounded-full transition-all ${
                index === activeIndex
                  ? 'w-8 bg-[#007CA0]'
                  : 'w-2 bg-slate-300 hover:bg-slate-400'
              }`}
              aria-label={`Go to image ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </>
  );
};
